/**
 * routes/Analytics — the see-everything analytics terminal (TA.2). One page that turns
 * the desk's full record into charts an operator (or an AI) can read end-to-end:
 *
 *  - headline KPI tiles (trades, winrate, profit factor, net R, avg R, max drawdown);
 *  - equity curve + underwater drawdown over a selectable window;
 *  - realised P&L over time (per-day bars + cumulative line, in R);
 *  - per-symbol / per-setup breakdowns (winrate · avg R · net R);
 *  - the R-multiple distribution and the MAE/MFE excursion scatter;
 *  - the win/loss/scratch split with the profit factor in the hole;
 *  - decision analytics: the candidate→committee→ENTER funnel, the NO-TRADE reason
 *    breakdown, Signal & Arbiter confidence histograms, and per-role scorecards;
 *  - an EXPORT button that downloads the whole surface as JSON for offline AI analysis.
 *
 * Everything is derived by the pure helpers in lib/analytics; while the desk has zero
 * closed trades every trade-fed panel shows an honest "NO DATA YET", never a fake bar.
 * Reads /api/equity-curve, /api/trades, /api/decisions, /api/stats (TA.1); the export
 * prefers /api/export and falls back to a client-assembled bundle when it 404s.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type {
  AgentScorecard,
  DecisionsResponse,
  EquityCurveResponse,
  EquityRange,
  StatsResponse,
  TradesResponse,
} from '../api/types';
import { usePoll } from '../api/usePoll';
import { useShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { StatTile } from '../components/StatTile';
import { DimBar } from '../components/charts/DimBar';
import type { DimMode } from '../components/charts/DimBar';
import { Donut } from '../components/charts/Donut';
import type { DonutSlice } from '../components/charts/Donut';
import { DrawdownChart } from '../components/charts/DrawdownChart';
import { EquityChart } from '../components/charts/EquityChart';
import { Funnel } from '../components/charts/Funnel';
import { Histogram } from '../components/charts/Histogram';
import { PnlChart } from '../components/charts/PnlChart';
import { ScatterMaeMfe } from '../components/charts/ScatterMaeMfe';
import {
  breakdownBy,
  confidenceHistogram,
  dailyPnl,
  decisionFunnel,
  drawdown,
  excursionScatter,
  noTradeReasons,
  rHistogram,
  roleConfidence,
  tradeKpis,
} from '../lib/analytics';
import type { BreakdownMetric } from '../lib/analytics';
import { ago, pctPlain } from '../lib/format';

const RANGES: readonly { key: EquityRange; label: string }[] = [
  { key: '7d', label: '7D' },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'all', label: 'All' },
];

const DIMS: readonly { key: 'symbol' | 'setupType'; label: string }[] = [
  { key: 'symbol', label: 'Symbol' },
  { key: 'setupType', label: 'Setup' },
];

const METRICS: readonly { key: BreakdownMetric; label: string; mode: DimMode }[] = [
  { key: 'winrate', label: 'Winrate', mode: 'winrate' },
  { key: 'avgR', label: 'Avg R', mode: 'avgR' },
  { key: 'netR', label: 'Net R', mode: 'avgR' },
];

const ROLE_LABEL: Record<string, string> = {
  signal: 'Signal', bear: 'Bear-case', risk: 'Risk', news: 'News', historian: 'Historian', arbiter: 'Arbiter',
};

export function Analytics(): JSX.Element {
  const live = useShell();
  const [range, setRange] = useState<EquityRange>('all');
  const [dim, setDim] = useState<'symbol' | 'setupType'>('setupType');
  const [metric, setMetric] = useState<BreakdownMetric>('netR');
  const [exporting, setExporting] = useState(false);

  const curve = usePoll<EquityCurveResponse>((s) => api.equityCurve(range, s), { intervalMs: 30_000 });
  // 200 is the backend page cap (paginationSchema.limit.max in src/web/api.ts). Requesting
  // more returns HTTP 400 and the analytics breakdowns silently lose their trade rows.
  const trades = usePoll<TradesResponse>((s) => api.trades(s, 200), { intervalMs: 30_000 });
  const decisions = usePoll<DecisionsResponse>((s) => api.decisions(s, 200), { intervalMs: 20_000 });
  const stats = usePoll<StatsResponse>((s) => api.stats(s), { intervalMs: 60_000 });

  const tradeRows = trades.data?.trades ?? [];
  const decisionRows = decisions.data?.decisions ?? [];
  const points = curve.data?.points ?? [];

  const kpis = useMemo(() => tradeKpis(tradeRows), [tradeRows]);
  const days = useMemo(() => dailyPnl(tradeRows), [tradeRows]);
  const dd = useMemo(() => drawdown(points), [points]);
  const bars = useMemo(() => breakdownBy(tradeRows, (t) => (dim === 'symbol' ? t.symbol : t.setupType), metric), [tradeRows, dim, metric]);
  const rHist = useMemo(() => rHistogram(tradeRows), [tradeRows]);
  const scatter = useMemo(() => excursionScatter(tradeRows), [tradeRows]);
  const funnel = useMemo(() => decisionFunnel(decisionRows), [decisionRows]);
  const reasons = useMemo(() => noTradeReasons(decisionRows), [decisionRows]);
  const signalHist = useMemo(() => confidenceHistogram(roleConfidence(decisionRows, 'signal')), [decisionRows]);
  const arbiterHist = useMemo(() => confidenceHistogram(roleConfidence(decisionRows, 'arbiter')), [decisionRows]);
  const scorecards = stats.data?.scorecards ?? [];

  // The max drawdown headline prefers the derived 'all' curve; falls back to the overview.
  const maxDd = points.length >= 2 ? Math.abs(dd.maxDrawdown) : (live.overview?.drawdownPct ?? null);

  const donutSlices: DonutSlice[] = [
    { key: 'win', label: 'Wins', value: kpis.wins, color: 'var(--profit)' },
    { key: 'loss', label: 'Losses', value: kpis.losses, color: 'var(--loss)' },
    { key: 'be', label: 'Scratch', value: kpis.breakeven, color: 'var(--text-muted)' },
  ];

  const loadingAll = trades.loading && decisions.loading && stats.loading && curve.loading;
  const source = live.overview?.source ?? (stats.data?.seed ? 'demo-seed' : undefined);

  async function handleExport(): Promise<void> {
    setExporting(true);
    try {
      let bundle: unknown;
      try {
        bundle = await api.export();
      } catch {
        // Endpoint not live yet — assemble the same shape from what the page already holds.
        bundle = {
          generatedBy: 'trade-kharvyn-frontend',
          source: source ?? 'unknown',
          overview: live.overview ?? null,
          equityCurve: curve.data ?? null,
          trades: tradeRows,
          decisions: decisionRows,
          stats: stats.data ?? null,
          derived: { kpis, dailyPnl: days, funnel, noTradeReasons: reasons },
          note: '/api/export unavailable — bundle assembled client-side from loaded data',
        };
      }
      downloadJson(bundle, 'trade-kharvyn-analytics.json');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Analytics</h1>
          <span className="page-head__sub">
            {trades.updatedAt ? `updated ${ago(trades.updatedAt)}` : 'loading…'}
            {source === 'demo-seed' && <span className="badge badge--neutral" style={{ marginLeft: 8 }}>demo seed</span>}
          </span>
        </div>
        <div className="page-head__actions">
          <button type="button" className="btn btn--ghost" onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? 'Exporting…' : '↓ Export JSON'}
          </button>
        </div>
      </div>

      {tradeRows.length === 0 && !loadingAll && (
        <div className="banner-note section" role="status">
          <strong>No closed trades yet.</strong> The desk has run {decisionRows.length} decision{decisionRows.length === 1 ? '' : 's'} but
          taken no trades — trade-fed panels below show <span className="mono">NO DATA YET</span> until the first fill closes.
          Decision analytics are live now.
        </div>
      )}

      {/* ── headline KPIs ──────────────────────────────────────────────────── */}
      <div className="stat-grid stat-grid--6 section">
        <StatTile label="Total trades" value={kpis.total} sub="closed, all-time" tone="neutral" />
        <StatTile
          label="Win rate"
          value={kpis.winrate != null ? pctPlain(kpis.winrate, 1) : '—'}
          sub={`${kpis.wins}W · ${kpis.losses}L${kpis.breakeven ? ` · ${kpis.breakeven}BE` : ''}`}
          tone={kpis.winrate != null ? (kpis.winrate >= 0.5 ? 'profit' : 'loss') : 'neutral'}
        />
        <StatTile
          label="Profit factor"
          value={kpis.profitFactor != null ? kpis.profitFactor.toFixed(2) : '—'}
          sub="gross R won ÷ lost"
          tone={kpis.profitFactor != null ? (kpis.profitFactor >= 1 ? 'profit' : 'loss') : 'neutral'}
        />
        <StatTile
          label="Net R"
          value={kpis.total ? `${kpis.netR >= 0 ? '+' : '−'}${Math.abs(kpis.netR).toFixed(2)}R` : '—'}
          sub="sum realized R"
          tone={kpis.total ? (kpis.netR >= 0 ? 'profit' : 'loss') : 'neutral'}
        />
        <StatTile
          label="Avg R"
          value={kpis.avgR != null ? `${kpis.avgR >= 0 ? '+' : '−'}${Math.abs(kpis.avgR).toFixed(2)}R` : '—'}
          sub="per closed trade"
          tone={kpis.avgR != null ? (kpis.avgR >= 0 ? 'profit' : 'loss') : 'neutral'}
        />
        <StatTile
          label="Max drawdown"
          value={maxDd != null ? `−${pctPlain(maxDd, 1)}` : '—'}
          sub="peak to trough"
          tone={maxDd != null && maxDd > 0 ? 'warning' : 'neutral'}
        />
      </div>

      {/* ── equity + drawdown ──────────────────────────────────────────────── */}
      <Card
        title="Equity & drawdown"
        hint={source === 'demo-seed' ? 'demo seed' : 'live'}
        actions={
          <div className="seg" role="group" aria-label="Range">
            {RANGES.map((r) => (
              <button key={r.key} type="button" className={`seg__btn${range === r.key ? ' is-active' : ''}`} aria-pressed={range === r.key} onClick={() => setRange(r.key)}>
                {r.label}
              </button>
            ))}
          </div>
        }
      >
        {points.length >= 2 ? (
          <>
            <EquityChart points={points} caption="Account equity" />
            <div style={{ height: 12 }} />
            <DrawdownChart points={dd.points} maxDrawdown={dd.maxDrawdown} />
          </>
        ) : curve.loading ? (
          <div className="state"><span className="spinner" aria-hidden /></div>
        ) : (
          <div className="state"><span className="muted">Not enough equity history to plot this range.</span></div>
        )}
      </Card>

      {/* ── P&L over time ──────────────────────────────────────────────────── */}
      <Card title="Realised P&L over time" hint="per day · cumulative · R">
        {days.length > 0 ? <PnlChart days={days} /> : <EmptyPanel />}
      </Card>

      {/* ── breakdown ──────────────────────────────────────────────────────── */}
      <Card
        title="Breakdown"
        hint={`by ${dim === 'symbol' ? 'symbol' : 'setup'}`}
        actions={
          <div className="seg-row">
            <div className="seg" role="group" aria-label="Dimension">
              {DIMS.map((d) => (
                <button key={d.key} type="button" className={`seg__btn${dim === d.key ? ' is-active' : ''}`} aria-pressed={dim === d.key} onClick={() => setDim(d.key)}>
                  {d.label}
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label="Metric">
              {METRICS.map((m) => (
                <button key={m.key} type="button" className={`seg__btn${metric === m.key ? ' is-active' : ''}`} aria-pressed={metric === m.key} onClick={() => setMetric(m.key)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
        }
      >
        {bars.length > 0 ? (
          <DimBar data={bars} mode={metric === 'winrate' ? 'winrate' : 'avgR'} />
        ) : (
          <EmptyPanel />
        )}
      </Card>

      {/* ── distributions ──────────────────────────────────────────────────── */}
      <div className="grid-2 section">
        <Card title="R-multiple distribution" hint="0.5R bins">
          {rHist.length > 0 ? <Histogram bins={rHist} mode="diverging" unit="R" ariaLabel="Distribution of realised R multiples" /> : <EmptyPanel />}
        </Card>
        <Card title="Outcome split" hint={`${kpis.total} trades`}>
          <Donut
            slices={donutSlices}
            centerValue={kpis.profitFactor != null ? kpis.profitFactor.toFixed(2) : '—'}
            centerLabel="profit factor"
          />
        </Card>
      </div>

      {/* ── excursions ─────────────────────────────────────────────────────── */}
      <Card title="MAE / MFE excursion scatter" hint="each dot = one trade">
        {scatter.length > 0 ? <ScatterMaeMfe points={scatter} /> : <EmptyPanel />}
      </Card>

      {/* ── decision analytics ─────────────────────────────────────────────── */}
      <h2 className="section-head">Decision analytics</h2>
      <div className="grid-2--even section">
        <Card title="Decision funnel" hint={`${decisionRows.length} decisions`}>
          <Funnel stages={funnel} />
        </Card>
        <Card title="NO-TRADE reasons" hint="why the desk passed">
          {reasons.length > 0 ? (
            <div className="dbar">
              {reasons.map((r) => {
                const top = reasons[0]!.count || 1;
                return (
                  <div className="dbar__row" key={r.key}>
                    <span className="dbar__label" title={r.label}>{r.label}</span>
                    <div className="dbar__track">
                      <span className="dbar__fill dbar__fill--seq" style={{ width: `${(r.count / top) * 100}%` }} title={`${r.label}: ${r.count}`} />
                    </div>
                    <span className="dbar__val num">{r.count}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="state"><span className="muted">No NO-TRADE decisions in range.</span></div>
          )}
        </Card>
      </div>

      <div className="grid-2--even section">
        <Card title="Signal confidence" hint="all decisions">
          {signalHist.some((b) => b.count > 0) ? <Histogram bins={signalHist} mode="sequential" unit="" height={200} ariaLabel="Signal confidence distribution" /> : <EmptyPanel />}
        </Card>
        <Card title="Arbiter confidence" hint="all decisions">
          {arbiterHist.some((b) => b.count > 0) ? <Histogram bins={arbiterHist} mode="sequential" unit="" height={200} ariaLabel="Arbiter confidence distribution" /> : <EmptyPanel />}
        </Card>
      </div>

      <Card title="Committee scorecards" hint="call vs eventual outcome" flush>
        {scorecards.length > 0 ? <ScorecardTable rows={scorecards} /> : <div className="card__body"><EmptyPanel /></div>}
      </Card>
    </>
  );
}

function EmptyPanel(): JSX.Element {
  return (
    <div className="state state--empty">
      <span className="empty-tag">NO DATA YET</span>
      <span className="muted">Populates once trades close.</span>
    </div>
  );
}

/** Per-role scorecard: precision & recall as paired accent bars (magnitude, one hue). */
function ScorecardTable({ rows }: { rows: readonly AgentScorecard[] }): JSX.Element {
  const ordered = [...rows].sort((a, b) => (b.precision ?? 0) - (a.precision ?? 0));
  return (
    <div className="scorecards">
      <div className="scorecards__head">
        <span>Role</span><span>n</span><span>Precision</span><span>Recall</span>
      </div>
      {ordered.map((r) => (
        <div className="scorecards__row" key={r.role}>
          <span className="scorecards__role">{ROLE_LABEL[r.role] ?? r.role}</span>
          <span className="num muted">{r.n}</span>
          <MetricBar value={r.precision} />
          <MetricBar value={r.recall} />
        </div>
      ))}
    </div>
  );
}

function MetricBar({ value }: { value: number | null }): JSX.Element {
  if (value == null) return <span className="muted scorecards__na">n/a</span>;
  return (
    <span className="scorecards__metric" title={pctPlain(value, 1)}>
      <span className="scorecards__bar-track"><span className="scorecards__bar" style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }} /></span>
      <span className="num scorecards__pct">{pctPlain(value, 0)}</span>
    </span>
  );
}

function downloadJson(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
